import chroma from 'chroma-js';

const adjectives = [
  'Radiant', 'Mystic', 'Cosmic', 'Vibrant', 'Mellow', 'Funky', 'Electric', 'Velvet',
  'Neon', 'Golden', 'Crimson', 'Azure', 'Frosty', 'Lazy', 'Zen', 'Breezy', 'Dazzling',
  'Wicked', 'Dreamy', 'Urban', 'Tropical', 'Retro', 'Stellar', 'Glossy', 'Matte',
  'Majestic', 'Hidden', 'Drunken', 'Dancing', 'Sleepy', 'Hyper', 'Chill', 'Savage'
];

const nouns = [
  'Sunset', 'Nebula', 'Ocean', 'Forest', 'Glow', 'Vibe', 'Dream', 'Night', 'Pulse',
  'Mist', 'Wave', 'Flame', 'Spark', 'Echo', 'Void', 'Galaxy', 'Horizon', 'Petal',
  'Rain', 'Thunder', 'Shadow', 'Light', 'Gem', 'Stone', 'Bloom', 'Aura', 'Soul',
  'Pixel', 'Logic', 'Magic', 'Fever', 'Bliss', 'Rhythm', 'Storm', 'Breeze'
];

/**
 * Generates a quirky, random name for a color palette.
 * @returns {string} A two-word quirky palette name.
 */
export function generateQuirkyName() {
  const adj = adjectives[Math.floor(Math.random() * adjectives.length)];
  const noun = nouns[Math.floor(Math.random() * nouns.length)];
  return `${adj} ${noun}`;
}

// The standard CSS named colors. Used to label individual swatches with the
// closest recognizable color name, the way Coolors shows "Frosted Mint" etc.
const NAMED_COLORS = {
  black: '#000000', white: '#ffffff', red: '#ff0000', lime: '#00ff00', blue: '#0000ff',
  yellow: '#ffff00', cyan: '#00ffff', magenta: '#ff00ff', silver: '#c0c0c0', gray: '#808080',
  maroon: '#800000', olive: '#808000', green: '#008000', purple: '#800080', teal: '#008080',
  navy: '#000080', orange: '#ffa500', gold: '#ffd700', crimson: '#dc143c', tomato: '#ff6347',
  coral: '#ff7f50', salmon: '#fa8072', 'dark orange': '#ff8c00', orchid: '#da70d6',
  violet: '#ee82ee', plum: '#dda0dd', indigo: '#4b0082', 'slate blue': '#6a5acd',
  'royal blue': '#4169e1', 'dodger blue': '#1e90ff', 'sky blue': '#87ceeb',
  'steel blue': '#4682b4', turquoise: '#40e0d0', aquamarine: '#7fffd4',
  'sea green': '#2e8b57', 'forest green': '#228b22', 'lime green': '#32cd32',
  'spring green': '#00ff7f', chartreuse: '#7fff00', 'olive drab': '#6b8e23',
  khaki: '#f0e68c', 'pale goldenrod': '#eee8aa', wheat: '#f5deb3', tan: '#d2b48c',
  peru: '#cd853f', chocolate: '#d2691e', sienna: '#a0522d', brown: '#a52a2a',
  firebrick: '#b22222', 'indian red': '#cd5c5c', 'rosy brown': '#bc8f8f',
  'hot pink': '#ff69b4', 'deep pink': '#ff1493', pink: '#ffc0cb', 'light pink': '#ffb6c1',
  lavender: '#e6e6fa', periwinkle: '#ccccff', thistle: '#d8bfd8', 'powder blue': '#b0e0e6',
  'light blue': '#add8e6', 'light cyan': '#e0ffff', honeydew: '#f0fff0', mintcream: '#f5fffa',
  'mint cream': '#f5fffa', azure: '#f0ffff', aliceblue: '#f0f8ff', 'alice blue': '#f0f8ff',
  'ghost white': '#f8f8ff', seashell: '#fff5ee', beige: '#f5f5dc', linen: '#faf0e6',
  'antique white': '#faebd7', 'navajo white': '#ffdead', moccasin: '#ffe4b5',
  bisque: '#ffe4c4', 'peach puff': '#ffdab9', 'misty rose': '#ffe4e1',
  gainsboro: '#dcdcdc', 'light gray': '#d3d3d3', 'dark gray': '#a9a9a9',
  'dim gray': '#696969', 'slate gray': '#708090', 'light slate gray': '#778899',
  'midnight blue': '#191970', 'cadet blue': '#5f9ea0', 'medium aquamarine': '#66cdaa',
  'dark sea green': '#8fbc8f', 'pale green': '#98fb98', 'light green': '#90ee90',
  'yellow green': '#9acd32', 'green yellow': '#adff2f', 'dark khaki': '#bdb76b',
  goldenrod: '#daa520', 'dark goldenrod': '#b8860b', 'sandy brown': '#f4a460',
  'burly wood': '#deb887', 'blanched almond': '#ffebcd', 'papaya whip': '#ffefd5',
  cornsilk: '#fff8dc', 'lemon chiffon': '#fffacd', 'light yellow': '#ffffe0',
  'light goldenrod': '#fafad2', ivory: '#fffff0', snow: '#fffafa', 'floral white': '#fffaf0',
  'old lace': '#fdf5e6', 'medium purple': '#9370db', 'blue violet': '#8a2be2',
  'dark violet': '#9400d3', 'dark orchid': '#9932cc', 'medium orchid': '#ba55d3',
  fuchsia: '#ff00ff', 'pale violet red': '#db7093', 'medium violet red': '#c71585',
  'dark slate blue': '#483d8b', 'medium slate blue': '#7b68ee', 'cornflower blue': '#6495ed',
  'deep sky blue': '#00bfff', 'light sky blue': '#87cefa', 'medium blue': '#0000cd',
  'dark blue': '#00008b', 'medium spring green': '#00fa9a', 'medium sea green': '#3cb371',
  'light sea green': '#20b2aa', 'dark cyan': '#008b8b', 'dark turquoise': '#00ced1',
  'medium turquoise': '#48d1cc', 'pale turquoise': '#afeeee', 'dark green': '#006400',
  'dark olive green': '#556b2f', 'light coral': '#f08080', 'dark salmon': '#e9967a',
  'light salmon': '#ffa07a', 'orange red': '#ff4500', 'dark red': '#8b0000',
  'dark magenta': '#8b008b', 'saddle brown': '#8b4513', 'dark slate gray': '#2f4f4f',
};

const _entries = Object.entries(NAMED_COLORS).map(([name, hex]) => ({
  name,
  lab: chroma(hex).lab(),
}));

const _cache = new Map();

function titleCase(str) {
  return str.replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Find the closest named color to a hex value (perceptual LAB distance).
 * @param {string} hex
 * @returns {string} A human-readable, title-cased color name.
 */
export function nearestColorName(hex) {
  if (!hex) return '';
  const key = hex.toLowerCase();
  if (_cache.has(key)) return _cache.get(key);

  let target;
  try {
    target = chroma(hex).lab();
  } catch (e) {
    return '';
  }

  let best = null;
  let bestDist = Infinity;
  for (const entry of _entries) {
    const dl = target[0] - entry.lab[0];
    const da = target[1] - entry.lab[1];
    const db = target[2] - entry.lab[2];
    const dist = dl * dl + da * da + db * db;
    if (dist < bestDist) {
      bestDist = dist;
      best = entry.name;
    }
  }

  const result = titleCase(best);
  _cache.set(key, result);
  return result;
}
